import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import useLogoUrl from "@/hooks/useLogoUrl";
import AdminSidebar, { Tab } from "./components/AdminSidebar";
import DashboardOverview from "./components/DashboardOverview";
import HomepageEditor from "./components/HomepageEditor";
import AboutEditor from "./components/AboutEditor";
import ProjectsEditor from "./components/ProjectsEditor";
import PartnersEditor from "./components/PartnersEditor";
import MessagesManager from "./components/MessagesManager";
import SiteSettings from "./components/SiteSettings";
import ChangeLog from "./components/ChangeLog";
import UsersManager from "./components/UsersManager";
import LiveSupportManager from "./components/LiveSupportManager";
import { messages } from "@/mocks/messages";
import { userStore, AuthSession } from "@/utils/userStore";
import { siteData } from "@/store/siteData";
import { adminPollSessions } from "@/utils/chatApi";
import { isFeatureEnabled } from "@/pages/dev-panel/page";
import AdsManager from "./components/AdsManager";
import NavigationManager from "./components/NavigationManager";

// ── Bildirim sesi ayarları ────────────────────────────────────────────────────
export function playAdminNotificationGlobal() {
  try {
    const settingsRaw = localStorage.getItem("ygNotifSettings");
    const settings = settingsRaw ? JSON.parse(settingsRaw) : {};
    const volume: number = settings.volume ?? 0.25;
    const tone: string = settings.tone ?? "double-ding";
    if (volume === 0) return;

    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

    const beep = (t: number, freq: number, dur: number) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g);
      g.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      g.gain.setValueAtTime(volume, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + dur);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + dur);
    };

    if (tone === "double-ding") {
      beep(0, 880, 0.3);
      beep(0.15, 880, 0.3);
    } else if (tone === "single-ding") {
      beep(0, 880, 0.4);
    } else if (tone === "high-ping") {
      beep(0, 1200, 0.2);
    } else if (tone === "low-boop") {
      beep(0, 440, 0.35);
      beep(0.18, 660, 0.3);
    } else if (tone === "chime") {
      beep(0, 523, 0.2);
      beep(0.12, 659, 0.2);
      beep(0.24, 784, 0.3);
    }
  } catch { /* sessizce */ }
}

const VALID_TABS: Tab[] = ["dashboard", "homepage", "about", "projects", "partners", "messages", "livesupport", "settings", "changelog", "users", "ads", "navigation"];

export default function KontrolPaneli() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const logoUrl = useLogoUrl();
  const [session, setSession] = useState<AuthSession | null>(() => userStore.getSession());
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const tabParam = searchParams.get("tab") as Tab | null;
    if (tabParam && VALID_TABS.includes(tabParam)) return tabParam;
    const saved = sessionStorage.getItem("yg_admin_tab") as Tab | null;
    return saved && VALID_TABS.includes(saved) ? saved : "dashboard";
  });
  const setAndSaveTab = (t: Tab) => {
    setActiveTab(t);
    sessionStorage.setItem("yg_admin_tab", t);
  };
  const [inspectorId, setInspectorId] = useState<number | null>(() => {
    const idParam = searchParams.get("id");
    return idParam ? Number(idParam) : null;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── Global destek bildirimi ─────────────────────────────────────────────────
  const [supportUnread, setSupportUnread] = useState(0);
  const prevSupportUnread = useRef(0);

  const pollSupport = useCallback(async () => {
    try {
      const res = await adminPollSessions();
      const total = res.totalUnread;
      if (total > prevSupportUnread.current) {
        playAdminNotificationGlobal();
      }
      prevSupportUnread.current = total;
      setSupportUnread(total);
    } catch { /* sessizce */ }
  }, []);

  useEffect(() => {
    if (!session) return;
    pollSupport();
    const t = setInterval(pollSupport, 4000);
    return () => clearInterval(t);
  }, [session, pollSupport]);

  useEffect(() => {
    const tabParam = searchParams.get("tab") as Tab | null;
    const idParam = searchParams.get("id");
    if (tabParam && VALID_TABS.includes(tabParam)) setAndSaveTab(tabParam);
    setInspectorId(idParam ? Number(idParam) : null);
  }, [searchParams]);

  const newMessages = messages.filter((m) => m.status === "new").length;

  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async () => {
    setLoginError("");
    setLoginLoading(true);
    try {
      const newSession = await userStore.login(loginForm.username, loginForm.password);
      await siteData.load();
      setSession(newSession);
      // Support rolunde otomatik Destek Merkezi'ne yönlendir (feature açıksa)
      if (newSession.role === "support" && isFeatureEnabled("livesupport")) {
        setAndSaveTab("livesupport");
      } else if (newSession.role === "support") {
        setAndSaveTab("messages");
      }
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : "Kullanıcı adı veya şifre hatalı.");
      setLoginForm((p) => ({ ...p, password: "" }));
    }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    await userStore.logout();
    setSession(null);
    setLoginForm({ username: "", password: "" });
  };

  // ── Login Screen ────────────────────────────────────────────────────────────

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A1020] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img
              src={logoUrl}
              alt="Yenigül Yapı"
              className="h-14 w-auto object-contain mx-auto mb-6"
            />
            <h1 className="text-white text-xl font-bold">Yönetim Paneli</h1>
            <p className="text-[#7B8FAF] text-sm mt-1">Giriş yapmak için bilgilerinizi girin</p>
          </div>

          <div className={`bg-[#141E35] border rounded-2xl p-8 space-y-4 ${loginError ? "border-red-500/40" : "border-[#2B3654]"}`}>
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Kullanıcı Adı</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#3B82F6]">
                  <i className="ri-user-3-line text-sm"></i>
                </div>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => { setLoginForm((p) => ({ ...p, username: e.target.value })); setLoginError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLogin(); }}
                  placeholder="kullanici_adi"
                  className={`w-full bg-[#0F1729] border text-white text-sm rounded-lg pl-9 pr-4 py-3 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398] ${loginError ? "border-red-500/60" : "border-[#2B3654]"}`}
                />
              </div>
            </div>

            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Şifre</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#3B82F6]">
                  <i className="ri-lock-line text-sm"></i>
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={loginForm.password}
                  onChange={(e) => { setLoginForm((p) => ({ ...p, password: e.target.value })); setLoginError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLogin(); }}
                  placeholder="••••••••"
                  className={`w-full bg-[#0F1729] border text-white text-sm rounded-lg pl-9 pr-10 py-3 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398] ${loginError ? "border-red-500/60" : "border-[#2B3654]"}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3B82F6] cursor-pointer w-4 h-4 flex items-center justify-center"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {loginError && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-3 py-2.5 rounded-lg">
                <i className="ri-error-warning-line text-sm flex-shrink-0"></i>
                {loginError}
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={loginLoading}
              className="w-full bg-[#3B82F6] hover:bg-[#3d4e6a] text-white font-medium py-3 rounded-lg transition-colors cursor-pointer text-sm whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {loginLoading && <i className="ri-loader-4-line animate-spin"></i>}
              {loginLoading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>

            <p className="text-[#5E7398] text-xs text-center pt-1">
              Varsayılan: <span className="text-[#7B8FAF]">admin</span> / <span className="text-[#7B8FAF]">yenigul2026</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Panel ──────────────────────────────────────────────────────────────

  const tabTitle: Record<Tab, string> = {
    dashboard: "Genel Bakış",
    homepage: "Ana Sayfa İçerikleri",
    about: "Hakkımızda Sayfası",
    projects: "Projeler",
    partners: "Stratejik Ortaklar",
    navigation: "Sayfa Yönetimi",
    messages: "Mesajlar",
    livesupport: "Destek Merkezi",
    ads: "Reklam Yönetimi",
    settings: "Site Ayarları",
    changelog: "Değişiklik Günlüğü",
    users: "Kullanıcılar & Güvenlik",
  };

  return (
    <div className="min-h-screen bg-[#0A1020] flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Sidebar — mobilde fixed, desktop'ta sticky */}
      <div className={`fixed lg:sticky lg:top-0 lg:self-start top-0 left-0 h-screen z-50 transition-transform duration-300 lg:translate-x-0 flex-shrink-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <AdminSidebar
          activeTab={activeTab}
          onTabChange={(tab) => { setAndSaveTab(tab); setSidebarOpen(false); }}
          newMessages={newMessages}
          supportUnread={supportUnread}
          userRole={session.role}
        />
      </div>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#0F1729] border-b border-[#2B3654] px-5 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden w-9 h-9 flex items-center justify-center text-[#7B8FAF] hover:text-white transition-colors cursor-pointer"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ri-menu-3-line text-xl"></i>
            </button>
            <div className="hidden sm:flex items-center gap-2 text-[#5E7398] text-xs">
              <i className="ri-map-pin-2-line text-[#3B82F6]"></i>
              <span>{tabTitle[activeTab]}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {isFeatureEnabled("site_inspector") && session.role !== "support" && (
              <button
                onClick={() => navigate("/site-inspector")}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-medium rounded-lg cursor-pointer hover:bg-emerald-500/25 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-cursor-line text-sm"></i>
                </div>
                <span>Görsel İnspektör</span>
              </button>
            )}

            <p className="text-[#465886] text-xs hidden lg:block">Değişiklikler anında kaydedilir</p>

            {/* Destek bildirimi — global, her menüde */}
            {supportUnread > 0 && activeTab !== "livesupport" && (
              <button
                onClick={() => setAndSaveTab("livesupport")}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-500/15 border border-red-500/30 text-red-300 text-xs font-medium rounded-lg cursor-pointer hover:bg-red-500/25 transition-all animate-pulse whitespace-nowrap"
                title="Bekleyen destek talepleri"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-customer-service-2-fill text-sm"></i>
                </div>
                <span>{supportUnread} destek</span>
              </button>
            )}

            {newMessages > 0 && (
              <button
                onClick={() => setAndSaveTab("messages")}
                className="relative w-9 h-9 flex items-center justify-center text-[#7B8FAF] hover:text-white cursor-pointer"
              >
                <i className="ri-notification-3-line text-xl"></i>
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#3B82F6] rounded-full text-white text-xs flex items-center justify-center font-bold">{newMessages}</span>
              </button>
            )}

            {isFeatureEnabled("changelog") && session.role === "admin" && (
              <button
                onClick={() => setAndSaveTab("changelog")}
                className={`relative w-9 h-9 flex items-center justify-center cursor-pointer transition-colors ${activeTab === "changelog" ? "text-[#3B82F6]" : "text-[#7B8FAF] hover:text-white"}`}
                title="Değişiklik Günlüğü"
              >
                <i className="ri-history-line text-xl"></i>
              </button>
            )}

            <div className="flex items-center gap-2 pl-2 border-l border-[#2B3654] ml-1">
              <button
                onClick={() => setAndSaveTab("users")}
                className="flex items-center gap-2 text-[#7B8FAF] hover:text-white text-xs transition-colors cursor-pointer px-2 py-1.5 rounded-lg hover:bg-white/5 whitespace-nowrap"
                title="Kullanıcı Ayarları"
              >
                <div className="w-6 h-6 rounded-full bg-[#3B82F6]/40 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {session.displayName.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:inline">{session.displayName}</span>
                {session.role === "admin" && (
                  <span className="hidden md:inline text-amber-400/70 text-xs">Admin</span>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-[#7B8FAF] hover:text-white text-sm transition-colors cursor-pointer p-1.5 rounded-lg hover:bg-white/5"
                title="Çıkış Yap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-logout-box-r-line text-sm"></i>
                </div>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-5 lg:p-8 overflow-y-auto">
          {activeTab === "dashboard" && <DashboardOverview />}
          {activeTab === "homepage" && <HomepageEditor />}
          {activeTab === "about" && <AboutEditor />}
          {activeTab === "projects" && <ProjectsEditor initialId={inspectorId} />}
          {activeTab === "partners" && <PartnersEditor initialId={inspectorId} />}
          {activeTab === "messages" && <MessagesManager />}
          {activeTab === "livesupport" && <LiveSupportManager externalUnread={supportUnread} onUnreadChange={setSupportUnread} userRole={session.role} />}
          {activeTab === "settings" && <SiteSettings userRole={session.role} />}
          {activeTab === "changelog" && <ChangeLog />}
          {activeTab === "ads" && <AdsManager />}
          {activeTab === "navigation" && <NavigationManager />}
          {activeTab === "users" && (
            <UsersManager
              currentUserId={session.userId}
              currentRole={session.role}
            />
          )}
        </main>
      </div>
    </div>
  );
}
